import * as AnalyticsTracker from '../../analytics/analyticsTracker';
import { ADBConfig, __DEBUG_ADOBE_ANALYTICS } from '../../environment';
import {
  AdobeAnalytics,
  AdobeAppLabels,
  AdobeLabels,
  AudioItem,
  MediaType,
} from '../../appTypings';
import { hasADB } from '../../analytics/analyticsTracker';
import { PlayerAnalyticsStateType, SubtitleItem } from '../../appTypings';
import { LoggedInUser } from '../../../common/types';
import { getUserIdForYleSk } from '../../analytics/analytics-utils';

const getStreamType = (isLive: boolean, isAudio: boolean) =>
  isLive ? 'live' : isAudio ? 'aod' : 'vod';

const getMediaInfo = (
  adobeLabels: AdobeLabels,
  programId: string,
  mediaDuration: number,
  mediaType: MediaType,
  isLiveMedia: boolean
): unknown => {
  const { yle_interaction, yle_media_name, yle_service_id } = adobeLabels;

  const isAudio = mediaType === 'AudioObject';
  const isContinueWatching = yle_interaction === 'player.continue.watching';

  const mediaObject = window.ADB.Media.createMediaObject(
    yle_media_name || yle_service_id || 'unknown',
    programId,
    mediaDuration / 1000,
    getStreamType(isLiveMedia, isAudio),
    isAudio ? 'audio' : 'video'
  );

  if (isContinueWatching) {
    mediaObject[window.ADB.Media.MediaObjectKey.MediaResumed] = true;
  }
  return mediaObject;
};

const getStateName = (
  type: PlayerAnalyticsStateType,
  item: SubtitleItem | AudioItem
) =>
  `${type === 'audio' ? 'audio' : 'text'}.${item.language}.${item.kind.toLowerCase()}`;

const adobeAnalytics: AdobeAnalytics = {
  hasSession: false,
  adobeLabels: {},
  programId: '',
  mediaDuration: 0,
  mediaType: 'VideoObject',
  isLiveMedia: false,
  tracker: undefined,
  appMeasurement: undefined,
  playerStates: [],
  initAnalytics() {
    if (!window.AppMeasurement || !window.Visitor || !window.ADB) {
      window.elasticApm?.captureError(new Error(`analytics not ready at init`));
    }
    const appMeasurement = new window.AppMeasurement(ADBConfig.analytics.rsids);
    appMeasurement.visitor = window.Visitor.getInstance(
      ADBConfig.marketingCloud.org,
      {
        disableIdSyncs: true,
        disableThirdPartyCookies: true,
        disableThirdPartyCalls: true,
      }
    );
    appMeasurement.trackingServer = ADBConfig.analytics.server;
    appMeasurement.charSet = ADBConfig.analytics.charset;
    appMeasurement.debugLogging = __DEBUG_ADOBE_ANALYTICS;
    this.appMeasurement = appMeasurement;

    const mediaConfig = new window.ADB.MediaConfig();
    mediaConfig.trackingServer = ADBConfig.mediaHeartbeat.server;
    mediaConfig.playerName = ADBConfig.mediaHeartbeat.playerName;
    mediaConfig.channel = ADBConfig.mediaHeartbeat.channel;
    mediaConfig.ssl = ADBConfig.mediaHeartbeat.ssl;
    mediaConfig.debugLogging = __DEBUG_ADOBE_ANALYTICS;

    window.ADB.Media.configure(mediaConfig, this.appMeasurement);
  },
  initMediaAnalytics(
    playerLabels: AdobeLabels,
    programId: string,
    seek: number | undefined,
    user: LoggedInUser | null | undefined,
    appLabels: AdobeAppLabels,
    defaultAudio: AudioItem | null,
    mediaType: MediaType,
    mediaDuration: number,
    isLiveMedia: boolean
  ) {
    if (!hasADB()) {
      return;
    }

    const userId = getUserIdForYleSk(user);

    const { language: audioLang, kind: audioType } = defaultAudio || {};

    this.adobeLabels = {
      ...playerLabels,
      ...appLabels,
      yle_audio_language: audioLang || playerLabels.yle_language || '',
      yle_audio_track_type: audioType || 'AudioTrack',
      ['yle.vrsContent']: mediaType === 'AudioObject' ? 'audio' : 'video',
      ...(user?.isSubAccount && user.age && { profile_age_group: user.age }),
    };
    if (seek) {
      this.adobeLabels['yle_interaction'] = 'player.continue.watching';
    }
    if (userId) {
      this.adobeLabels['yle_sk'] = userId;
    }
    this.programId = programId;
    this.mediaDuration = mediaDuration;
    this.isLiveMedia = isLiveMedia;
    this.mediaType = mediaType;
    this.activateSession();
  },
  initPlayerStates() {
    this.playerStates = [];
    const fullscreenState = window.ADB.Media.createStateObject(
      window.ADB.Media.PlayerState.FullScreen
    );
    this.playerStates.push({ current: true, stateObject: fullscreenState });
    this.tracker.trackEvent(window.ADB.Media.Event.StateStart, fullscreenState);
  },
  updatePlayerState(
    type: PlayerAnalyticsStateType,
    item: AudioItem | SubtitleItem | null
  ) {
    const currentState = this.playerStates.find(
      state => state.type === type && state.current
    );
    if (currentState) {
      this.tracker.trackEvent(
        window.ADB.Media.Event.StateEnd,
        currentState.stateObject
      );
      currentState.current = false;
    }
    if (item) {
      const newStateObject = window.ADB.Media.createStateObject(
        getStateName(type, item)
      );
      const existingStateObject = this.playerStates.find(
        state =>
          state.stateObject['customstate.name'] ===
          newStateObject['customstate.name']
      );
      if (existingStateObject) {
        existingStateObject.current = true;
      } else {
        this.playerStates.push({
          type,
          current: true,
          stateObject: newStateObject,
        });
      }
      this.tracker.trackEvent(
        window.ADB.Media.Event.StateStart,
        newStateObject
      );
    }
  },
  activateSession() {
    if (this.hasSession) {
      return;
    }
    const mediaInfo = getMediaInfo(
      this.adobeLabels,
      this.programId,
      this.mediaDuration,
      this.mediaType,
      this.isLiveMedia
    );
    const tracker = window.ADB.Media.getInstance();
    this.tracker = tracker;
    AnalyticsTracker.trackSessionStart(mediaInfo, this.adobeLabels);
    this.hasSession = true;
  },
  endSession() {
    AnalyticsTracker.trackSessionEnd();
    this.hasSession = false;
  },
  completed() {
    if (!this.hasSession) {
      return;
    }
    AnalyticsTracker.trackComplete();
  },
  played() {
    if (!this.hasSession) {
      return;
    }
    AnalyticsTracker.trackPlay();
  },
  paused() {
    if (!this.hasSession) {
      return;
    }
    AnalyticsTracker.trackPause();
  },
  seekStart() {
    if (!this.hasSession || !hasADB()) {
      return;
    }
    AnalyticsTracker.trackEvent(window.ADB.Media.Event.SeekStart);
  },
  seekEnd() {
    if (!this.hasSession) {
      return;
    }
    AnalyticsTracker.trackEvent(window.ADB.Media.Event.SeekComplete);
  },
  bufferStart() {
    if (!this.hasSession || !hasADB()) {
      return;
    }
    AnalyticsTracker.trackEvent(window.ADB.Media.Event.BufferStart);
  },
  bufferEnd() {
    if (!this.hasSession || !hasADB()) {
      return;
    }
    AnalyticsTracker.trackEvent(window.ADB.Media.Event.BufferComplete);
  },
  ended() {
    if (!this.hasSession) {
      return;
    }
    this.endSession();
  },
};

export default adobeAnalytics;
